import { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  FilterAlt as FilterAltIcon,
  ModelTraining as ModelTrainingIcon,
  DataObject as DataObjectIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid2,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { styled } from '@mui/material/styles';

import PageHeader from '../../components/PageHeader';

// ---------------------------------------------------------------------------
// Inline GQL documents (no codegen needed until backend is accessible)
// ---------------------------------------------------------------------------
const SW_CENTROID_STATS = gql`
  query swCentroidStats {
    swCentroidStats {
      version
      threshold
      auc
      recall
      precision
      filterRate
      separation
      swCount
      nonSwCount
      computedAt
      model
      threshold95
      threshold97
      threshold99
      threshold995
    }
  }
`;

const SW_PREDICTION_SUMMARY = gql`
  query swPredictionSummary($timeRange: String) {
    swPredictionSummary(timeRange: $timeRange) {
      timeRange
      total
      swCount
      nonSwCount
      filterRate
      avgSimilarity
    }
  }
`;

const SW_PREDICTION_TIME_SERIES = gql`
  query swPredictionTimeSeries($timeRange: String) {
    swPredictionTimeSeries(timeRange: $timeRange) {
      timeRange
      dataPoints {
        timestamp
        total
        swCount
        nonSwCount
        filterRate
        avgSimilarity
      }
    }
  }
`;

const SW_VALIDATION_FEEDBACK = gql`
  query swValidationFeedback {
    swValidationFeedback {
      tp
      fp
      tn
      fn
      total
      accuracy
      precision
      recall
    }
  }
`;

const SW_TRAINING_DATA_STATS = gql`
  query swTrainingDataStats {
    swTrainingDataStats {
      swCount
      swEmbedded
      nonSwCount
      nonSwEmbedded
      fpCount
      fpEmbedded
      lastRetrain
    }
  }
`;

const SW_FALSE_POSITIVE_COMPANIES = gql`
  query swFalsePositiveCompanies($limit: Int) {
    swFalsePositiveCompanies(limit: $limit) {
      companyId
      domain
      companyName
      validationDate
    }
  }
`;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    transform: 'translateY(-4px)',
  },
}));

const GradientCard = styled(Card)<{ gradient?: string }>(({ theme, gradient }) => ({
  height: '100%',
  borderRadius: 16,
  background:
    gradient ||
    `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
  color: '#FFFFFF',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    transform: 'translateY(-4px)',
  },
}));

const MetricBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmt = (num: number | null | undefined, decimals = 1): string => {
  if (num === null || num === undefined) return 'N/A';
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toFixed(decimals);
};

const fmtPct = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return 'N/A';
  return `${(num * 100).toFixed(1)}%`;
};

const fmtPctRaw = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return 'N/A';
  return `${num.toFixed(1)}%`;
};

const fmtDate = (d: string | null | undefined): string => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleString();
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const TimeRangeSelector = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const ranges = [
    { value: '1h', label: '1 Hour' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
  ];
  return (
    <Stack direction="row" spacing={1}>
      {ranges.map(r => (
        <Chip
          key={r.value}
          label={r.label}
          variant={value === r.value ? 'filled' : 'outlined'}
          color={value === r.value ? 'primary' : 'default'}
          onClick={() => onChange(r.value)}
          sx={{ cursor: 'pointer' }}
        />
      ))}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Confusion Matrix
// ---------------------------------------------------------------------------
const ConfusionMatrix = ({ tp, fp, tn, fn }: { tp: number; fp: number; tn: number; fn: number }) => {
  const theme = useTheme();
  const total = tp + fp + tn + fn;
  const cell = (label: string, value: number, color: string) => (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: alpha(color, 0.1),
        textAlign: 'center',
        border: `1px solid ${alpha(color, 0.3)}`,
      }}
    >
      <Typography variant="h5" fontWeight={700} color={color}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label} ({total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)
      </Typography>
    </Box>
  );

  return (
    <Grid2 container spacing={1}>
      <Grid2 size={6}>{cell('True Positive', tp, theme.palette.success.main)}</Grid2>
      <Grid2 size={6}>{cell('False Positive', fp, theme.palette.error.main)}</Grid2>
      <Grid2 size={6}>{cell('False Negative', fn, theme.palette.warning.main)}</Grid2>
      <Grid2 size={6}>{cell('True Negative', tn, theme.palette.info.main)}</Grid2>
    </Grid2>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const SWClassifierPage = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('24h');

  // Queries
  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchStats,
  } = useQuery(SW_CENTROID_STATS, { fetchPolicy: 'cache-and-network' });

  const {
    data: summaryData,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery(SW_PREDICTION_SUMMARY, {
    variables: { timeRange },
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: tsData,
    loading: tsLoading,
    refetch: refetchTs,
  } = useQuery(SW_PREDICTION_TIME_SERIES, {
    variables: { timeRange },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: feedbackData,
    loading: feedbackLoading,
    refetch: refetchFeedback,
  } = useQuery(SW_VALIDATION_FEEDBACK, { fetchPolicy: 'cache-and-network' });

  const {
    data: trainingData,
    loading: trainingLoading,
    refetch: refetchTraining,
  } = useQuery(SW_TRAINING_DATA_STATS, { fetchPolicy: 'cache-and-network' });

  const {
    data: fpData,
    loading: fpLoading,
    refetch: refetchFp,
  } = useQuery(SW_FALSE_POSITIVE_COMPANIES, {
    variables: { limit: 50 },
    fetchPolicy: 'cache-and-network',
  });

  const handleRefresh = () => {
    refetchStats();
    refetchSummary();
    refetchTs();
    refetchFeedback();
    refetchTraining();
    refetchFp();
  };

  const isLoading = statsLoading && summaryLoading && tsLoading && feedbackLoading && trainingLoading;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  const stats = statsData?.swCentroidStats;
  const summary = summaryData?.swPredictionSummary;
  const dataPoints = tsData?.swPredictionTimeSeries?.dataPoints || [];
  const feedback = feedbackData?.swValidationFeedback;
  const training = trainingData?.swTrainingDataStats;
  const fpCompanies = fpData?.swFalsePositiveCompanies || [];

  return (
    <Box>
      <PageHeader title="SW Classifier" subtitle="Embedding centroid pre-filter dashboard" />
      <Box sx={{ p: 3 }}>
        {/* Controls */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Stack>

        {/* ============================================================== */}
        {/* Section 1: Centroid Status */}
        {/* ============================================================== */}
        <Typography variant="h6" fontWeight={600} mb={2}>
          Centroid Status
        </Typography>
        <Grid2 container spacing={2} mb={4}>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <GradientCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <ModelTrainingIcon />
                  <Typography variant="h6" fontWeight={600}>
                    Active Centroid v{stats?.version ?? '?'}
                  </Typography>
                </Stack>
                <Grid2 container spacing={2}>
                  {[
                    { label: 'Threshold', value: stats?.threshold?.toFixed(4) },
                    { label: 'AUC', value: stats?.auc?.toFixed(3) },
                    { label: 'Target Recall', value: '99.5%' },
                    { label: 'Precision', value: fmtPct(stats?.precision) },
                    { label: 'Filter Rate', value: fmtPct(stats?.filterRate) },
                    { label: 'Separation', value: stats?.separation?.toFixed(4) },
                  ].map(m => (
                    <Grid2 key={m.label} size={{ xs: 6, sm: 4, md: 2 }}>
                      <MetricBox>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {m.label}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {m.value ?? 'N/A'}
                        </Typography>
                      </MetricBox>
                    </Grid2>
                  ))}
                </Grid2>
                <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
                  Model: {stats?.model ?? 'N/A'} | Last retrain: {fmtDate(stats?.computedAt)}
                </Typography>
              </CardContent>
            </GradientCard>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Threshold Comparison
                </Typography>
                {[
                  { label: '95% recall', value: stats?.threshold95?.toFixed(4) },
                  { label: '97% recall', value: stats?.threshold97?.toFixed(4) },
                  { label: '99% recall', value: stats?.threshold99?.toFixed(4) },
                  { label: '99.5% recall (active)', value: stats?.threshold995?.toFixed(4), active: true },
                ].map(t => (
                  <Stack key={t.label} direction="row" justifyContent="space-between" py={0.5}>
                    <Typography variant="body2" fontWeight={t.active ? 700 : 400}>
                      {t.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={t.active ? 700 : 400}
                      color={t.active ? 'primary' : 'text.primary'}
                    >
                      {t.value ?? 'N/A'}
                    </Typography>
                  </Stack>
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  SW samples: {fmt(stats?.swCount, 0)} | Non-SW: {fmt(stats?.nonSwCount, 0)}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid2>
        </Grid2>

        {/* ============================================================== */}
        {/* Section 2: Prediction Activity (Time Series) */}
        {/* ============================================================== */}
        <Typography variant="h6" fontWeight={600} mb={2}>
          Prediction Activity
        </Typography>
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            {tsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : dataPoints.length > 0 ? (
              <LineChart
                height={300}
                xAxis={[
                  {
                    data: dataPoints.map((d: any) => new Date(d.timestamp)),
                    scaleType: 'time',
                  },
                ]}
                series={[
                  {
                    data: dataPoints.map((d: any) => d.swCount),
                    label: 'SW (passed)',
                    color: theme.palette.success.main,
                  },
                  {
                    data: dataPoints.map((d: any) => d.nonSwCount),
                    label: 'Non-SW (filtered)',
                    color: theme.palette.error.main,
                  },
                ]}
              />
            ) : (
              <Typography color="text.secondary" textAlign="center" py={4}>
                No prediction data available for this time range.
              </Typography>
            )}
          </CardContent>
        </StyledCard>

        {/* ============================================================== */}
        {/* Section 3: Prediction Summary Cards */}
        {/* ============================================================== */}
        <Typography variant="h6" fontWeight={600} mb={2}>
          Prediction Summary
        </Typography>
        <Grid2 container spacing={2} mb={4}>
          {[
            {
              label: 'Total Predictions',
              value: fmt(summary?.total, 0),
              icon: <DataObjectIcon />,
              color: theme.palette.primary.main,
            },
            {
              label: 'Passed (SW)',
              value: fmt(summary?.swCount, 0),
              icon: <CheckCircleIcon />,
              color: theme.palette.success.main,
            },
            {
              label: 'Filtered (Non-SW)',
              value: fmt(summary?.nonSwCount, 0),
              icon: <FilterAltIcon />,
              color: theme.palette.error.main,
            },
            {
              label: 'Filter Rate',
              value: fmtPct(summary?.filterRate),
              icon: <TrendingUpIcon />,
              color: theme.palette.warning.main,
            },
          ].map(card => (
            <Grid2 key={card.label} size={{ xs: 6, md: 3 }}>
              <StyledCard>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Box sx={{ color: card.color }}>{card.icon}</Box>
                    <Typography variant="caption" color="text.secondary">
                      {card.label}
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700}>
                    {card.value}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid2>
          ))}
        </Grid2>

        {/* ============================================================== */}
        {/* Section 4 + 5: Validation Feedback + Training Data (side by side) */}
        {/* ============================================================== */}
        <Grid2 container spacing={2} mb={4}>
          {/* Validation Feedback */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Validation Feedback
            </Typography>
            <StyledCard>
              <CardContent>
                {feedbackLoading ? (
                  <CircularProgress />
                ) : feedback && feedback.total > 0 ? (
                  <>
                    <ConfusionMatrix tp={feedback.tp} fp={feedback.fp} tn={feedback.tn} fn={feedback.fn} />
                    <Stack direction="row" spacing={2} mt={2} justifyContent="center">
                      {[
                        { label: 'Accuracy', value: fmtPct(feedback.accuracy) },
                        { label: 'Precision', value: fmtPct(feedback.precision) },
                        { label: 'Recall', value: fmtPct(feedback.recall) },
                      ].map(m => (
                        <Box key={m.label} textAlign="center">
                          <Typography variant="h6" fontWeight={700}>
                            {m.value}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.label}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    No validation feedback data yet.
                  </Typography>
                )}
              </CardContent>
            </StyledCard>
          </Grid2>

          {/* Training Data */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Training Data
            </Typography>
            <StyledCard>
              <CardContent>
                {trainingLoading ? (
                  <CircularProgress />
                ) : training ? (
                  <>
                    <Grid2 container spacing={1}>
                      {[
                        {
                          label: 'SW Pages',
                          count: training.swCount,
                          embedded: training.swEmbedded,
                          color: theme.palette.success.main,
                        },
                        {
                          label: 'Non-SW Pages',
                          count: training.nonSwCount,
                          embedded: training.nonSwEmbedded,
                          color: theme.palette.info.main,
                        },
                        {
                          label: 'False Positive Pages',
                          count: training.fpCount,
                          embedded: training.fpEmbedded,
                          color: theme.palette.error.main,
                        },
                      ].map(t => (
                        <Grid2 key={t.label} size={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: alpha(t.color, 0.08),
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h5" fontWeight={700} color={t.color}>
                              {t.count}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t.label}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {t.embedded} embedded
                            </Typography>
                          </Box>
                        </Grid2>
                      ))}
                    </Grid2>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      Last retrain: {fmtDate(training.lastRetrain)}
                    </Typography>
                  </>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    No training data stats available.
                  </Typography>
                )}
              </CardContent>
            </StyledCard>
          </Grid2>
        </Grid2>

        {/* ============================================================== */}
        {/* Section 6: False Positive Companies */}
        {/* ============================================================== */}
        <Typography variant="h6" fontWeight={600} mb={2}>
          False Positive Companies
        </Typography>
        <StyledCard>
          <CardContent>
            {fpLoading ? (
              <CircularProgress />
            ) : fpCompanies.length > 0 ? (
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Domain</TableCell>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Validation Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fpCompanies.map((c: any) => (
                      <TableRow key={c.companyId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {c.domain}
                          </Typography>
                        </TableCell>
                        <TableCell>{c.companyName}</TableCell>
                        <TableCell>{fmtDate(c.validationDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" textAlign="center" py={2}>
                No false positive companies found.
              </Typography>
            )}
          </CardContent>
        </StyledCard>
      </Box>
    </Box>
  );
};

export default SWClassifierPage;
